import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, RefreshCw, Check, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, money, money2, productImage } from "@/lib/api";
import { PageHeader, SectionTitle, Thumb, Pill, Field, EmptyState } from "@/components/Bits";

const input = "w-full rounded-sm border border-line bg-white px-3 py-2 text-sm outline-none focus:border-lime";
const STATUSES = ["Draft", "Active Comparison", "Selected", "Archived"];

function AddItemForm({ scenarioId, projectSelections, onDone }) {
  const [mode, setMode] = useState("swap");
  const [form, setForm] = useState({ replaces_product_id: "", name: "", unit_price: "", quantity: 1, image: "", product_id: "" });

  const submit = async (e) => {
    e.preventDefault();
    try {
      const body =
        mode === "existing"
          ? { product_id: form.product_id }
          : {
              name: form.name,
              unit_price: Number(form.unit_price) || 0,
              quantity: Number(form.quantity) || 1,
              image: form.image || null,
              replaces_product_id: mode === "swap" ? form.replaces_product_id || null : null,
            };
      await api.post(`/scenarios/${scenarioId}/items`, body);
      toast.success(mode === "swap" ? "Product swapped" : "Product added");
      setForm({ replaces_product_id: "", name: "", unit_price: "", quantity: 1, image: "", product_id: "" });
      onDone();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <form onSubmit={submit} className="card-surface space-y-5 p-6" data-testid="scenario-add-item-form">
      <div className="flex flex-wrap gap-2">
        {[
          ["swap", "Swap a product"],
          ["add", "Add a potential product"],
          ["existing", "Add from project / library"],
        ].map(([value, label]) => (
          <button
            key={value}
            type="button"
            data-testid={`scenario-item-mode-${value}`}
            onClick={() => setMode(value)}
            className={`rounded-sm border px-3 py-1.5 text-xs transition-colors ${
              mode === value ? "border-lime bg-lime text-ink" : "border-line text-stone hover:border-fog"
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        {mode === "swap" && (
          <Field label="Replace which selection?">
            <select data-testid="scenario-swap-target" required value={form.replaces_product_id} onChange={(e) => setForm({ ...form, replaces_product_id: e.target.value })} className={input}>
              <option value="">Select…</option>
              {projectSelections.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </Field>
        )}
        {mode === "existing" ? (
          <Field label="Product">
            <select data-testid="scenario-existing-product" required value={form.product_id} onChange={(e) => setForm({ ...form, product_id: e.target.value })} className={input}>
              <option value="">Select…</option>
              {projectSelections.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </Field>
        ) : (
          <>
            <Field label="Product name">
              <input data-testid="scenario-item-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={input} />
            </Field>
            <Field label="Proposed client price (each)">
              <input data-testid="scenario-item-price" type="number" step="0.01" value={form.unit_price} onChange={(e) => setForm({ ...form, unit_price: e.target.value })} className={`num ${input}`} />
            </Field>
            <Field label="Quantity">
              <input data-testid="scenario-item-qty" type="number" min="1" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} className={`num ${input}`} />
            </Field>
            <Field label="Image URL (optional)">
              <input data-testid="scenario-item-image" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} className={input} />
            </Field>
          </>
        )}
      </div>
      <button data-testid="scenario-item-submit" type="submit" className="inline-flex items-center gap-2 rounded-sm bg-lime px-4 py-2.5 text-sm font-medium text-ink hover:bg-lime-dark">
        <Plus className="h-4 w-4" strokeWidth={1.5} /> {mode === "swap" ? "Swap" : "Add"}
      </button>
    </form>
  );
}

export default function ScenarioDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [scenario, setScenario] = useState(null);
  const [selections, setSelections] = useState([]);
  const [preflight, setPreflight] = useState(null);
  const [applying, setApplying] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/scenarios/${id}`);
      setScenario(data);
      const { data: prods } = await api.get("/products", { params: { project_id: data.project_id } });
      setSelections(prods.filter((p) => p.selection_status !== "Declined" && p.selection_status !== "Superseded"));
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const patch = async (body) => {
    try {
      const { data } = await api.patch(`/scenarios/${id}`, body);
      setScenario(data);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const removeItem = async (itemId) => {
    try {
      const { data } = await api.delete(`/scenarios/${id}/items/${itemId}`);
      setScenario(data);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const updateItem = async (itemId, body) => {
    try {
      const { data } = await api.patch(`/scenarios/${id}/items/${itemId}`, body);
      setScenario(data);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const openPreflight = async () => {
    try {
      const { data } = await api.get(`/scenarios/${id}/apply-preflight`);
      setPreflight(data);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const apply = async () => {
    setApplying(true);
    try {
      const { data } = await api.post(`/scenarios/${id}/apply`);
      toast.success(`Scenario applied — ${data.created} selection(s) created`);
      setPreflight(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setApplying(false);
    }
  };

  if (!scenario) return <div className="py-24 text-center text-sm text-fog">Loading scenario…</div>;

  const swaps = scenario.items.filter((i) => i.replaces_product_id);
  const byId = Object.fromEntries(selections.map((p) => [p.id, p]));

  return (
    <div className="space-y-10" data-testid="scenario-detail-page">
      <button onClick={() => navigate(`/projects/${scenario.project_id}?tab=Scenarios`)} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-stone hover:text-ink">
        <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.5} /> Back to project
      </button>

      <PageHeader
        overline={`Internal Scenario · ${scenario.space_name || scenario.project_name}`}
        title={scenario.name}
        subtitle={scenario.description}
        testId="scenario-header"
        actions={
          <>
            <select
              data-testid="scenario-status-select"
              value={scenario.status}
              onChange={(e) => patch({ status: e.target.value })}
              className="rounded-sm border border-line bg-white px-3 py-2.5 text-xs outline-none focus:border-lime"
            >
              {STATUSES.map((s) => <option key={s}>{s}</option>)}
            </select>
            <button
              data-testid="scenario-apply-btn"
              onClick={openPreflight}
              className="inline-flex items-center gap-2 rounded-sm bg-lime px-4 py-2.5 text-sm font-medium text-ink hover:bg-lime-dark"
            >
              <Check className="h-4 w-4" strokeWidth={1.5} /> Apply Scenario to Project
            </button>
          </>
        }
      />

      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-testid="scenario-totals">
        {[
          ["Scenario Total", money(scenario.total), null],
          ["Difference vs Baseline", scenario.difference_from_baseline == null ? "—" : `${scenario.difference_from_baseline > 0 ? "+" : ""}${money(scenario.difference_from_baseline)}`, scenario.baseline_name],
          [scenario.budget_reference, money(scenario.space_budget || scenario.project_budget), null],
          ["Difference vs Budget", scenario.difference_from_budget == null ? "—" : `${scenario.difference_from_budget > 0 ? "+" : ""}${money(scenario.difference_from_budget)}`, scenario.difference_from_budget != null && scenario.difference_from_budget <= 0 ? "Within budget" : "Over budget"],
        ].map(([label, value, hint]) => (
          <div key={label} className="card-surface p-6">
            <div className="overline">{label}</div>
            <div className="num mt-3 text-2xl text-ink">{value}</div>
            {hint && <div className="mt-2 text-xs text-fog">{hint}</div>}
          </div>
        ))}
      </div>

      {swaps.length > 0 && (
        <section>
          <SectionTitle>Visual Swaps</SectionTitle>
          <div className="grid gap-6 lg:grid-cols-2" data-testid="scenario-swaps">
            {swaps.map((i) => {
              const from = byId[i.replaces_product_id];
              const fromTotal = (from?.client_unit_price || 0) * (i.quantity || 1);
              const savings = Math.round((fromTotal - i.line_total) * 100) / 100;
              return (
                <div key={i.id} className="card-surface p-6" data-testid={`scenario-swap-${i.id}`}>
                  <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-4">
                    <div>
                      <div className="overline mb-3">Current</div>
                      <Thumb src={productImage(from)} alt={from?.name} className="h-24 w-full" />
                      <div className="mt-3 text-sm text-ink">{from?.name || "Removed selection"}</div>
                      <div className="num mt-1 text-xs text-stone">{money2(from?.client_unit_price || 0)} × {i.quantity}</div>
                    </div>
                    <RefreshCw className="h-4 w-4 text-fog" strokeWidth={1.5} />
                    <div>
                      <div className="overline mb-3">{scenario.name.split("—").pop().trim()}</div>
                      <Thumb src={i.image} alt={i.name} className="h-24 w-full" />
                      <div className="mt-3 text-sm text-ink">{i.name}</div>
                      <div className="num mt-1 text-xs text-stone">{money2(i.unit_price)} × {i.quantity}</div>
                    </div>
                  </div>
                  <div className={`mt-5 border-t border-line pt-4 text-sm ${savings >= 0 ? "text-lime-dark" : "text-clay"}`}>
                    {savings >= 0 ? `Savings: ${money2(savings)}` : `Increase: ${money2(Math.abs(savings))}`}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <section>
        <SectionTitle>Scenario Items</SectionTitle>
        {scenario.items.length === 0 ? (
          <EmptyState testId="scenario-items-empty" title="Nothing in this scenario yet" body="Add products below to start building the option." />
        ) : (
          <div className="card-surface overflow-x-auto scroll-thin">
            <table className="w-full min-w-[820px] text-sm" data-testid="scenario-items-table">
              <thead>
                <tr className="border-b border-line text-left">
                  {["", "Product", "Qty", "Client Price", "Shipping", "Tax", "Line Total", ""].map((h, i) => (
                    <th key={i} className="overline px-4 py-3">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {scenario.items.map((i) => (
                  <tr key={i.id} className="border-b border-line last:border-0" data-testid={`scenario-item-${i.id}`}>
                    <td className="px-4 py-3"><Thumb src={i.image} alt={i.name} className="h-10 w-12" /></td>
                    <td className="px-4 py-3">
                      <div className="text-ink">{i.name}</div>
                      {i.replaces_product_id && <div className="mt-1 text-[11px] text-fog">Replaces {byId[i.replaces_product_id]?.name || "a selection"}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <input
                        data-testid={`scenario-item-qty-${i.id}`}
                        type="number" min="1" defaultValue={i.quantity}
                        onBlur={(e) => updateItem(i.id, { quantity: Number(e.target.value) || 1 })}
                        className={`num w-16 ${input}`}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <input
                        data-testid={`scenario-item-price-${i.id}`}
                        type="number" step="0.01" defaultValue={i.unit_price}
                        onBlur={(e) => updateItem(i.id, { unit_price: Number(e.target.value) || 0 })}
                        className={`num w-28 ${input}`}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <input type="number" step="0.01" defaultValue={i.shipping} onBlur={(e) => updateItem(i.id, { shipping: Number(e.target.value) || 0 })} className={`num w-24 ${input}`} />
                    </td>
                    <td className="px-4 py-3">
                      <input type="number" step="0.01" defaultValue={i.tax} onBlur={(e) => updateItem(i.id, { tax: Number(e.target.value) || 0 })} className={`num w-24 ${input}`} />
                    </td>
                    <td className="num px-4 py-3 text-right text-ink">{money2(i.line_total)}</td>
                    <td className="px-4 py-3 text-right">
                      <button data-testid={`scenario-item-remove-${i.id}`} onClick={() => removeItem(i.id)} className="rounded-sm border border-line p-1.5 text-stone hover:border-clay hover:text-clay" aria-label="Remove item">
                        <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <AddItemForm scenarioId={id} projectSelections={selections} onDone={load} />

      {preflight && (
        <div className="fixed inset-0 z-[60] flex items-end justify-center p-0 sm:items-center sm:p-6" data-testid="scenario-preflight-modal">
          <div className="absolute inset-0 bg-ink/35 backdrop-blur-sm" onClick={() => setPreflight(null)} />
          <div className="card-surface relative max-h-[92vh] w-full max-w-2xl overflow-auto p-7">
            <h3 className="font-serif text-2xl text-ink">Apply “{scenario.name}” to the project?</h3>
            <div className="mt-5 grid gap-4 sm:grid-cols-3">
              {[
                ["Products Added", preflight.adds.length],
                ["Products Replaced", preflight.replaces.length],
                ["Products Removed", preflight.removes.length],
              ].map(([l, v]) => (
                <div key={l} className="border-l-2 border-line pl-4">
                  <div className="overline">{l}</div>
                  <div className="num mt-1 text-xl text-ink">{v}</div>
                </div>
              ))}
            </div>
            <div className="mt-5 border-t border-line pt-5 text-sm text-stone">
              Estimated budget difference{" "}
              <span className={`num ${preflight.budget_difference > 0 ? "text-clay" : "text-lime-dark"}`}>
                {preflight.budget_difference > 0 ? "+" : ""}{money2(preflight.budget_difference)}
              </span>
            </div>
            {!!preflight.price_changes.length && (
              <div className="mt-5 space-y-1 text-xs text-stone" data-testid="preflight-price-changes">
                {preflight.price_changes.map((c) => (
                  <div key={c.name}>{c.name}: {money2(c.from)} → {money2(c.to)}</div>
                ))}
              </div>
            )}
            {preflight.conflicts.length > 0 ? (
              <div className="mt-6 rounded-sm border border-[#E07A5F] bg-[#F6DED6] p-5" data-testid="preflight-conflicts">
                <div className="flex items-center gap-2 text-sm font-medium text-[#A8492E]">
                  <AlertTriangle className="h-4 w-4" strokeWidth={1.5} /> Resolve these first
                </div>
                <ul className="mt-3 space-y-2 text-xs text-[#A8492E]">
                  {preflight.conflicts.map((c) => (
                    <li key={c.product_id}>
                      <Link to={`/products/${c.product_id}`} className="underline">{c.name}</Link> — {c.reason}
                    </li>
                  ))}
                </ul>
                <p className="mt-3 text-xs text-[#A8492E]">
                  Approved, ordered and delivered items are never replaced automatically.
                </p>
              </div>
            ) : null}
            <div className="mt-7 flex gap-3">
              <button
                data-testid="preflight-confirm-btn"
                disabled={!preflight.can_apply || applying}
                onClick={apply}
                className="rounded-sm bg-lime px-5 py-2.5 text-sm font-medium text-ink hover:bg-lime-dark disabled:opacity-50"
              >
                {applying ? "Applying…" : "Confirm & Apply"}
              </button>
              <button onClick={() => setPreflight(null)} className="rounded-sm border border-line px-5 py-2.5 text-sm text-ink hover:bg-line/50">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function ScenarioCompare() {
  const [params] = useSearchParams();
  const ids = params.get("ids") || "";
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/scenarios/compare", { params: { ids } }).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  }, [ids]);

  if (!data) return <div className="py-24 text-center text-sm text-fog">Loading comparison…</div>;

  return (
    <div className="space-y-10" data-testid="scenario-compare-page">
      <PageHeader overline="Internal" title="Scenario Comparison" subtitle="Side by side, with the pieces you would actually be swapping." testId="compare-header" />

      <div className="grid gap-6" style={{ gridTemplateColumns: `200px repeat(${data.scenarios.length}, minmax(0, 1fr))` }} data-testid="compare-grid">
        <div />
        {data.scenarios.map((s) => (
          <div key={s.id} className="card-surface p-5">
            <Link to={`/scenarios/${s.id}`} className="font-serif text-lg text-ink hover:text-lime-dark">{s.name}</Link>
            <div className="num mt-3 text-2xl text-ink">{money(s.total)}</div>
            <div className={`num mt-1 text-xs ${s.difference_from_first > 0 ? "text-clay" : "text-lime-dark"}`}>
              {s.difference_from_first === 0 ? "Baseline" : `${s.difference_from_first > 0 ? "+" : ""}${money(s.difference_from_first)}`}
            </div>
            <Pill tone="bg-line text-stone">{s.status}</Pill>
          </div>
        ))}

        {data.rows.map((row, idx) => (
          <div key={idx} className="contents">
            <div className="flex items-center text-sm text-stone">{row.label}</div>
            {row.cells.map((cell, ci) => (
              <div key={ci} className="card-surface p-4" data-testid={`compare-cell-${idx}-${ci}`}>
                {cell ? (
                  <>
                    <Thumb src={cell.image} alt={cell.name} className="h-20 w-full" />
                    <div className="mt-3 text-sm text-ink">{cell.name}</div>
                    <div className="num mt-1 text-xs text-stone">{money2(cell.unit_price)} × {cell.quantity}</div>
                    <div className="num mt-2 text-sm text-ink">{money2(cell.line_total)}</div>
                  </>
                ) : (
                  <div className="py-8 text-center text-xs text-fog">Not included</div>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
